import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Download, X, Share2, Plus } from "lucide-react";
import useInstallPrompt from "@/hooks/useInstallPrompt";

const STORAGE_KEY = "afm-install-dismissed-at";
const DAYS_BEFORE_RE_SHOW = 14;

const InstallAppPrompt = () => {
  const { canInstall, installed, isIos, promptInstall } = useInstallPrompt();
  const [visible, setVisible] = useState(false);
  const [iosOpen, setIosOpen] = useState(false);

  useEffect(() => {
    if (installed || !canInstall) { setVisible(false); return; }
    const dismissedAt = parseInt(localStorage.getItem(STORAGE_KEY) || "0", 10);
    const snoozed = dismissedAt && (Date.now() - dismissedAt) < DAYS_BEFORE_RE_SHOW * 24 * 60 * 60 * 1000;
    if (snoozed) { setVisible(false); return; }
    // Tiny delay so the banner doesn't compete with first paint
    const t = setTimeout(() => setVisible(true), isIos ? 4000 : 2000);
    return () => clearTimeout(t);
  }, [canInstall, installed, isIos]);

  const snooze = () => {
    localStorage.setItem(STORAGE_KEY, String(Date.now()));
    setVisible(false);
    setIosOpen(false);
  };

  const install = async () => {
    const result = await promptInstall();
    if (result === "installed") snooze();
    else if (result === "ios") setIosOpen(true);
  };

  if (!visible) return null;

  return (
    <>
      <div
        data-testid="install-prompt"
        className="fixed left-3 right-3 bottom-3 z-40 sm:left-1/2 sm:-translate-x-1/2 sm:right-auto sm:w-[420px] bg-[#1A2024] text-white rounded-2xl shadow-2xl border border-white/10 backdrop-blur-sm overflow-hidden"
      >
        <div className="flex items-center gap-3 p-3 pr-2">
          <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center shrink-0">
            <img src="/logomark.png" alt="AfriMarket logo" className="w-7 h-7" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-sm">Install AfriMarket</div>
            <div className="text-xs text-white/70">Faster access, offline reads, real notifications.</div>
          </div>
          <Button
            data-testid="install-prompt-install"
            onClick={install}
            className="bg-[#D9923B] hover:bg-[#D9923B]/90 text-[#1A2024] font-semibold rounded-md h-9 px-3 text-xs"
          >
            <Download className="w-3.5 h-3.5 mr-1" /> Install
          </Button>
          <button
            data-testid="install-prompt-dismiss"
            onClick={snooze}
            className="text-white/50 hover:text-white p-2 rounded-md"
            aria-label="Dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {isIos && iosOpen && (
        <div
          data-testid="install-ios-modal"
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 bg-black/50"
          onClick={() => setIosOpen(false)}
        >
          <div className="bg-white rounded-2xl max-w-md w-full p-6 text-center" onClick={(e) => e.stopPropagation()}>
            <div className="w-12 h-12 mx-auto rounded-xl bg-[#1A2024] flex items-center justify-center mb-3">
              <img src="/logomark.png" alt="AfriMarket logo" className="w-8 h-8" />
            </div>
            <div className="font-display text-2xl mb-2">Install on your iPhone</div>
            <p className="text-sm text-muted-foreground mb-4">Two taps and you&apos;re done — Safari only.</p>
            <ol className="text-left text-sm space-y-3 mb-5">
              <li className="flex gap-3">
                <span className="w-6 h-6 rounded-full bg-[#C05A3C] text-white text-xs font-bold flex items-center justify-center shrink-0">1</span>
                <span>Tap the <Share2 className="inline w-4 h-4 -mt-1 mx-1" /> <b>Share</b> button at the bottom of Safari.</span>
              </li>
              <li className="flex gap-3">
                <span className="w-6 h-6 rounded-full bg-[#C05A3C] text-white text-xs font-bold flex items-center justify-center shrink-0">2</span>
                <span>Scroll and pick <Plus className="inline w-4 h-4 -mt-1 mx-1" /> <b>Add to Home Screen</b>.</span>
              </li>
            </ol>
            <Button onClick={snooze} className="w-full bg-[#C05A3C] hover:bg-[#D46B4E] text-white rounded-md">Got it</Button>
          </div>
        </div>
      )}
    </>
  );
};

export default InstallAppPrompt;
